import { ProductAlert, Product } from '../../types';
import * as React from 'react';
import { FormEvent } from 'react';
import { DateTime } from 'luxon';
import './AlertForm.scss';
import ItemEntry from './form/ItemEntry';
import FormGroup from './form/FormGroup';
import AlertInput from './form/AlertInput';
import { debounce } from 'lodash';
import { fetchProductNames } from 'services/ProductAlertService';

export type AlertFormProps = {
    alert: Partial<ProductAlert>;
    setAlert: (newAlert: ProductAlert | null) => void;
    onSubmit: (changes: AlertFormState['changes']) => void;
    onCancel: () => void;
    disabled?: boolean;
    /** True if the form should communicate that an alert is being created, false otherwise. */
    isNewAlert?: boolean;
};

type AlertFormState = {
    changes: Partial<ProductAlert>;
};

/** The date format used by date input elements. Note that changing this will not change the display format,
 * as it is only used for the format of the value, which should not be changed.
 */
const DATE_FORMAT = 'yyyy-MM-dd';

/** How long (in milliseconds) to wait before fetching names for products with item numbers. */
const NAME_COMPLETION_DELAY = 500;

const ACTIVE_VALUE = 'active';

export default class AlertForm extends React.Component<AlertFormProps, AlertFormState> {
    public static readonly defaultProps = {
        disabled: false,
        isNewAlert: true,
    };

    private static readonly converters:
        {[K in keyof ProductAlert]?: (rawValue: string) => ProductAlert[K]} = {
            active(value: string) { return value === ACTIVE_VALUE; },
            alertNumber(value: string) { return Number(value); },
            expiryDate(value: string) { return DateTime.fromFormat(value, DATE_FORMAT); }
        };

    /** All products which are pending having their name fetched. */
    private pendingNames: Set<Product['itemNumber']> = new Set();

    private scheduleNameCompletion = debounce(this.fetchNames.bind(this), NAME_COMPLETION_DELAY);

    constructor(props: AlertFormProps) {
        super(props);

        this.state = {
            changes: {},
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.addItem = this.addItem.bind(this);
    }

    public render() {
        const expiryDate = this.props.alert.expiryDate;

        return (
            <form className="alert-form" onSubmit={this.handleSubmit}>
                <h1>
                    {this.props.isNewAlert ? 'Add' : 'Edit'} Alert
                </h1>
                <h2>
                    Choose Status
                </h2>
                <FormGroup className="inline-radio">
                    <span>
                        <AlertInput
                            type="radio"
                            name="active"
                            value={ACTIVE_VALUE}
                            checked={this.props.alert.active}
                            disabled={this.props.disabled}
                            onChange={this.handleChange}
                        /> Active
                    </span>
                    <span>
                        <AlertInput
                            type="radio"
                            name="active"
                            value="inactive"
                            checked={!this.props.alert.active}
                            disabled={this.props.disabled}
                            onChange={this.handleChange}
                        /> Inactive
                    </span>
                </FormGroup>
                <h2>
                    Alert Details
                </h2>
                <FormGroup>
                    <label>
                        Date Created
                        <input
                            type="date"
                            value={this.props.alert.created ?
                                this.props.alert.created.toFormat(DATE_FORMAT) :
                                DateTime.utc().toFormat(DATE_FORMAT)}
                            readOnly={true}
                        />
                    </label>
                </FormGroup>
                <FormGroup>
                    <label>
                        Alert Number
                        <AlertInput
                            type="number"
                            min={1}
                            value={this.props.alert.alertNumber || ''}
                            name="alertNumber"
                            disabled={this.props.disabled}
                            onChange={this.handleChange}
                        />
                    </label>
                </FormGroup>
                <FormGroup>
                    <label>
                        Expiry Date
                        {expiryDate}
                        <AlertInput
                            type="date"
                            name="expiryDate"
                            value={expiryDate ? expiryDate.toFormat(DATE_FORMAT) : ''}
                            disabled={this.props.disabled}
                            onChange={this.handleChange}
                        />
                    </label>
                </FormGroup>
                <h2>
                    Item Details
                </h2>
                {(this.props.alert.itemInfo || []).map(({ itemName, itemNumber }, i) => {
                    return <ItemEntry
                        key={i}
                        itemName={itemName}
                        itemNumber={itemNumber}
                        onChange={(value) => { this.handleItemChange(value, i); }}
                        disabled={this.props.disabled}
                    />;
                })}
                <input
                    type="button"
                    onClick={this.addItem}
                    className="add-item-button"
                    value="+ Add another item"
                    disabled={this.props.disabled}
                />
                <FormGroup className="submit-group">
                    <input
                        type="button"
                        value="Discard Changes"
                        className="cancel-action"
                        onClick={this.props.onCancel}
                        disabled={this.props.disabled}
                    />
                    <input type="submit" value="Save and Publish" disabled={this.props.disabled} />
                </FormGroup>
            </form>
        );
    }

    public componentDidUpdate() {
        this.checkNonEmpty();
    }

    public componentDidMount() {
        this.checkNonEmpty();
    }

    private handleSubmit(event: FormEvent<HTMLFormElement>): void {
        // Call preventDefault since forms will attempt to make the request themselves, leading to a reload.
        event.preventDefault();
        this.props.onSubmit(this.state.changes);
    }

    private async fetchNames(): Promise<void> {
        const itemsNeedingNames = Array.from(this.pendingNames);
        if (itemsNeedingNames.length > 0) {
            const nameMap = await fetchProductNames(itemsNeedingNames);
            this.withNewAlert((alert) => {
                // Careful: this doesn't copy the 
                alert.itemInfo = alert.itemInfo.map((infoEntry) => {
                    if (nameMap.has(infoEntry.itemNumber)) {
                        return {
                            ...infoEntry,
                            itemName: nameMap.get(infoEntry.itemNumber) || '',
                        };
                    }
                    return infoEntry;
                });
            });
        }
    }

    private handleItemChange(newItemNumber: string, index: number): void {
        const currentItems = [...this.props.alert.itemInfo];
        if (currentItems[index].itemNumber !== newItemNumber) {
            this.pendingNames.delete(currentItems[index].itemNumber);
            currentItems[index] = { itemNumber: newItemNumber, itemName: null };
            this.pendingNames.add(newItemNumber);

            this.withNewAlert((newAlert) => {
                this.updateAlert({ itemInfo: currentItems }, newAlert);
            });
            this.scheduleNameCompletion();
        }
    }

    private checkNonEmpty(): void {
        if ((this.props.alert.itemInfo || []).length === 0) {
            // We can't show no items for the user to enter info on, so add one in the next tick if there aren't any.
            this.addItem();
        }
    }

    private addItem(): void {
        this.withNewAlert((alert) => {
            alert.itemInfo = [...alert.itemInfo, { itemNumber: '', itemName: null }];
        });
    }

    private withNewAlert(callback: (newAlert: ProductAlert, cancelUpdate: () => void) => void): void {
        if (this.props.alert.clone) {
            const newAlert = this.props.alert.clone();
            let shouldUpdate = true;
            callback(newAlert, () => {
                shouldUpdate = false;
            });
            if (shouldUpdate) {
                this.props.setAlert(newAlert);
            }
        }
    }

    private handleChange(event: FormEvent<HTMLInputElement>): void {
        const target = event.target as any;
        this.withNewAlert((newAlert, cancelUpdate) => {
            // Cast the target name to a key of the ProductAlert class so that the case strings are correct.
            if ((target.name as keyof ProductAlert) in AlertForm.converters) {
                const value = AlertForm.converters[target.name](target.value);
                this.updateAlert({ [target.name]: value }, newAlert);
            } else {
                cancelUpdate();
            }
        });
    }

    private updateAlert(changes: Partial<ProductAlert>, alert: ProductAlert): void {
        Object.assign(alert, changes);
        this.setState({ changes: { ...this.state.changes, ...changes } });
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/Alerts/AlertForm.tsx